import { useCallback } from 'react';
import { VALID_GAME_ID } from '@root/constants';
import { useAuth } from '@root/hooks';
import { getCurrencySymbol } from '@root/lib';
import { useSubscription } from '@root/providers';
import { useActions } from '@root/store/store';
import {
  BetState,
  CombinedRoundState,
  GameConfig,
  Market,
  MarketStatus,
  Round,
  RoundWinner,
  RunFundingStatusDTO,
  UserBalance,
  UserExposure,
  UserSelectionExposure,
} from '@root/store/types';
import { useGameSubscribeEmit } from '@shared/hooks';

export function useGameSubscriptions() {
  const { user, operatorId } = useAuth();
  const actions = useActions();

  const handleRoundUpdate = useCallback(
    (data: Round<CombinedRoundState>) => {
      actions.setRound(data);
    },
    [actions]
  );

  // updateMarkets scopes state.markets to the payload's own roundId, so
  // there's no need to gate on a round frame having arrived first — doing
  // so previously dropped market frames that beat their round frame.
  const handleMarketUpdate = useCallback(
    (data: Market) => {
      actions.updateMarkets(data);
      actions.setAvailableToBet(data.status === MarketStatus.OPEN);
    },
    [actions]
  );

  const handleBalanceUpdate = useCallback(
    (data: UserBalance) => {
      const currencySymbol = getCurrencySymbol(
        data.userCurrencyAmount.currencyCode
      );
      actions.setBalance({
        currencySymbol,
        amount: data.userCurrencyAmount.amount,
        userCurrency: data.userCurrencyAmount.currencyCode,
      });
      actions.setCurrencyCode(currencySymbol);
    },
    [actions]
  );

  const handleExposureUpdate = useCallback(
    (data: UserExposure) => {
      actions.setExposure({
        currencySymbol: getCurrencySymbol(data.userCurrency),
        amount: data.exposureF,
        userCurrency: data.userCurrency,
      });
    },
    [actions]
  );

  const handleSelectionExposureUpdate = useCallback(
    (data: UserSelectionExposure) => {
      actions.setSelectionExposure(data);
    },
    [actions]
  );

  const handleBetsUpdate = useCallback(
    (data: BetState) => {
      actions.setBets(data.bets);
    },
    [actions]
  );

  const handleGameConfigUpdate = useCallback(
    (data: GameConfig) => {
      actions.setGameConfig(data);
    },
    [actions]
  );

  const handleRoundWinnerUpdate = useCallback(
    (data: RoundWinner) => {
      actions.setRoundWinner(data);
    },
    [actions]
  );

  // Two different messages arrive on /game/notification. Only a funding one
  // carries status and journeyId.
  const handleGameNotification = useCallback(
    (data: unknown) => {
      const payload = data as Partial<RunFundingStatusDTO> & {
        action?: string;
      };
      if (payload?.status && payload?.journeyId) {
        actions.setFundingStatus(payload as RunFundingStatusDTO);
      }
    },
    [actions]
  );

  useSubscription<Round<CombinedRoundState>>(
    `/topic/game/${VALID_GAME_ID}/round`,
    handleRoundUpdate,
    []
  );
  useSubscription<Market>(
    `/topic/game/${VALID_GAME_ID}/market`,
    handleMarketUpdate,
    []
  );
  useSubscription<UserBalance>(
    user ? `/user/${user.id}/balance` : '',
    handleBalanceUpdate,
    []
  );
  useSubscription<UserExposure>(
    user ? `/user/${user.id}/exposure` : '',
    handleExposureUpdate,
    []
  );
  useSubscription<Market>(
    user ? `/user/${user.id}/game/market` : '',
    handleMarketUpdate,
    []
  );
  useSubscription<UserSelectionExposure>(
    user ? `/user/${user.id}/selection/exposure` : '',
    handleSelectionExposureUpdate,
    []
  );
  useSubscription<BetState>(
    user ? `/user/${user.id}/bets` : '',
    handleBetsUpdate,
    []
  );
  useSubscription<GameConfig>(
    operatorId
      ? `/topic/game/${VALID_GAME_ID}/operator/${operatorId}/config`
      : '',
    handleGameConfigUpdate,
    []
  );
  useSubscription<GameConfig>(
    user ? `/user/${user.id}/game/config` : '',
    handleGameConfigUpdate,
    []
  );
  useSubscription<Round<CombinedRoundState>>(
    user ? `/user/${user.id}/game/round` : '',
    handleRoundUpdate,
    []
  );
  useSubscription<RoundWinner>(
    user ? `/user/${user.id}/winning` : '',
    handleRoundWinnerUpdate,
    []
  );
  useSubscription<unknown>(
    user ? `/user/${user.id}/game/notification` : '',
    handleGameNotification,
    []
  );

  // Shared emit — waits for the user, then tells the backend to start streaming.
  useGameSubscribeEmit(VALID_GAME_ID);
}
